import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    Typography
} from "@mui/material/";
import { Update } from "@mui/icons-material";
import { strings } from "../../common/i18n";
import { format } from "../../common/lib";
import { AuditSummaryDownloadButton } from "../../common/components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ExcludeDialog from "./ExcludeDialog";
import styles from "./styles";
import CheckIcon from "@mui/icons-material/Check";
import NotFound from "../../common/components/NotFound";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { logToSentry } from "../../config/Sentry/";
import actions from "../../Store/actions";

const assignedFilter = ({ isAssigned, isExpected, isMandatory, isRelated }) =>
    isAssigned && isExpected && isMandatory && isRelated;

const mandatoryFilter = ({ isExpected, isMandatory, isRelated }) =>
    isExpected && isMandatory && isRelated;

type Props = {
    loading: boolean
};

/**
 * Edit Audit Detail Component
 */
export default ({ loading }: Props) => {
    let navigate = useNavigate();
    const dispatch = useDispatch();
    const [loadDetails, setLoadDetails] = useState(true);
    const {
        accounts,
        assetAuditors,
        auditAssets,
        auditLocations,
        audits,
        altAssets
    } = useSelector((state) => state);

    const [dialogOpen, setDialogOpen] = useState(false);
    const { pathname } = useLocation();
    const params = useParams();

    const data = useMemo(() => {
        return (() => {
            if (audits.item) {
                const auditLocationIds = auditLocations.data
                    .filter((item) => item.audit === audits.item.id)
                    .map((item) => item.id);
                const forAuditLocations = auditAssets.data.filter((item) =>
                    auditLocationIds.includes(item.auditLocation)
                );
                const assigned = forAuditLocations.filter(assignedFilter);
                const mandatory = forAuditLocations.filter(mandatoryFilter);

                const result = {
                    branch: audits.item.branch.name,
                    completedDate: format.dateTimeNew(
                        audits.item.completedDate,
                        "dateTime"
                    ),
                    completion: Math.round(
                        100 * (assigned.length / mandatory.length)
                    ),
                    totalAssets: mandatory.length,
                    totalAssigned: assigned.length,
                    unexpectedAssets: altAssets.data.length,
                    name: audits.item.name,
                    schedule: `${format.dateTimeNew(
                        audits.item.startDate
                    )} \u2014 ${format.dateTimeNew(audits.item.endDate)}`,
                    status: audits.item.status.name
                };
                setLoadDetails(false);
                return result;
            } else return null;
        })();
    }, [
        accounts,
        assetAuditors,
        auditAssets,
        auditLocations,
        audits,
        altAssets
    ]);

    const onDialogHide = () => setDialogOpen(false);
    const onDialogShow = () => setDialogOpen(true);

    const routeTo = (path) => {
        const auditPath = pathname
            .split("/")
            .slice(0, -1)
            .join("/")
            .replace(
                "edit",
                path === "inprogress" ? "inprogress" : params.auditId
            );
        return auditPath;
    };

    const onCheckAsset = () => {
        try {
            navigate(routeTo(""));
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On navigating check asset:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onCheckAsset"))
            );
        }
    };

    const onBack = () => {
        try {
            navigate(routeTo("inprogress"));
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On navigating to inprogess:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onBackAudits"))
            );
        }
    };

    // /** If no audit is found display not found message */
    if (!audits.item)
        return <NotFound message={strings("messages.auditNotFound")} />;

    if (!loadDetails)
        return (
            data && (
                <Box sx={styles.details}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography
                                variant="caption"
                                component="span"
                                mr={0.5}
                            >
                                <IconButton onClick={onBack}>
                                    <ArrowBackIcon color="primary" />
                                </IconButton>
                            </Typography>
                            <Typography component="span" variant="body1">
                                {data.name}
                            </Typography>
                            <Divider />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" component="span">
                                {strings("branch")} :
                            </Typography>
                            <Typography component="span" variant="body2">
                                {data.branch}
                            </Typography>

                            <Divider />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" component="span">
                                {strings("schedule")} :
                            </Typography>
                            <Typography component="span" variant="body2">
                                {data.schedule}
                            </Typography>
                            <Divider />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" component="span">
                                {strings("status")} :
                            </Typography>
                            <Typography component="span" variant="body2">
                                {data.status}
                            </Typography>
                            <Divider />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" component="span">
                                {strings("completion")} :
                            </Typography>
                            <Typography component="span" variant="body2">
                                {`${
                                    isNaN(data.completion) ? 0 : data.completion
                                }% (${data.totalAssigned} of ${
                                    data.totalAssets
                                } expected assets) ${
                                    data.unexpectedAssets > 0
                                        ? `+ ${data.unexpectedAssets} unexpected assets`
                                        : ""
                                }`}
                            </Typography>
                            <Divider />
                        </Grid>
                        <Grid item xs={6}>
                            <Button
                                startIcon={<Update />}
                                disabled={
                                    loading || Boolean(data.completedDate)
                                }
                                onClick={onDialogShow}
                                size="small"
                                variant="outlined"
                                fullWidth
                            >
                                {strings("editUncheckedAssets")}
                            </Button>
                        </Grid>
                        <Grid item xs={6}>
                            <Button
                                startIcon={<CheckIcon />}
                                onClick={onCheckAsset}
                                size="small"
                                variant="outlined"
                                fullWidth
                            >
                                {strings("checkedAssets")}
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <AuditSummaryDownloadButton />
                        </Grid>

                        {data.completedDate && (
                            <Grid item xs={12}>
                                <Typography variant="caption">
                                    {strings("completed")}
                                </Typography>
                                <Typography>{data.completedDate}</Typography>
                                <Divider />
                            </Grid>
                        )}
                    </Grid>
                    <ExcludeDialog onClose={onDialogHide} open={dialogOpen} />
                </Box>
            )
        );
};
